import React, { useCallback, useEffect, useMemo } from "react";
import { PageHeader } from "@ant-design/pro-layout";
import { debounce } from "lodash";
import {
  Alert,
  Col,
  Input,
  Layout,
  Row,
  Select,
  Space,
  Table,
  TablePaginationConfig,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { FilterValue, SorterResult } from "antd/es/table/interface";
import { Link, useSearchParams } from "react-router-dom";
import { WizardService } from "../../services/WizardService";
import { SpmInfo } from "../../types/spmInfo";
import { SpmIntegrationRecord } from "../../types/spmIntegrationInfo";
import { MEDIATION_ENGINES } from "../mapping/mediationEngines";
import { ResponsiveCard } from "../components/ResponsiveCard";
import { safeHref } from "../components/safeHref";

const { Content } = Layout;
const { Text } = Typography;

const SOURCES_DOC =
  "Mapping-scoped SPM SOURCE config (spm_info_v1): which Swift Package repo to crawl for a " +
  "given mapping + mediation engine. Edited on the Mapping page.";

const MATERIALIZED_DOC =
  "Crawler OUTPUT (spm_integration_info_v1): one row per resolved Swift package product, " +
  "anchored on exactly one adapter or one SDK. Read-only — written only by the SPM crawler, " +
  "which upserts and never deletes.";

// Maps a sortable Materialized-table column's `dataIndex` to the backend's `sort` query param
// (`GET /v4/spm/integrations?sort=...`). Deliberately explicit rather than relying on
// `dataIndex` happening to equal the backend key — `targetStatus` -> `target` does not.
const RECORD_SORT_KEY_BY_DATA_INDEX: Record<string, string> = {
  targetStatus: "target",
  product: "product",
  version: "version",
  platform: "platform",
};

// AntD's `Table` adds a synthetic `key` to each row via `dataSource={records.map((r) => ({
// ...r, key: r.id }))}` below — the `onChange` sorter it hands back is therefore typed against
// that shape, not the bare `SpmIntegrationRecord`.
type RecordRow = SpmIntegrationRecord & { key: number };

// Single source of truth for "where does this row's anchor live" — used by both the Target and
// Product cells so their links can never drift apart. Navigation only: nothing is created or
// written here.
const targetHref = (record: SpmIntegrationRecord): string =>
  record.targetKind === "adapter"
    ? `/wizard/adapter/${record.targetId}`
    : `/wizard/sdk/${record.targetId}`;

/**
 * SPM admin overview: the crawler's INPUT (sources) next to its OUTPUT (materialized rows).
 *
 * Two tables rather than one because they are different tables with different lifecycles —
 * conflating them is the mistake this page exists to prevent. A source row is hand-authored
 * config scoped to a `Mapping`; a materialized row is crawler output anchored on an
 * `Adapter` XOR an `Sdk`. A source with no materialized rows means the crawl found nothing to
 * bind, which is exactly the diagnosis this screen has to make possible.
 */
export const SpmPage = () => {
  // Corrected 2026-09-18 (KI-12): `mediation`/`target` used to be plain `React.useState` with no
  // link to the URL at all — the only `URLSearchParams` on this page were the outgoing ones built
  // in `fetchRecords` below to call the API, so a deep link like `?mediation=<engine>` opened the
  // page unfiltered. Both filters are now seeded from `useSearchParams()` on mount, and the Select
  // `onChange` handlers below write back into the same search params so the URL round-trips.
  const [searchParams, setSearchParams] = useSearchParams();

  const [sourcesLoading, setSourcesLoading] = React.useState<boolean>(false);
  const [sources, setSources] = React.useState<SpmInfo[]>([]);
  const [sourcesPagination, setSourcesPagination] =
    React.useState<TablePaginationConfig>({
      current: 1,
      pageSize: 20,
      showSizeChanger: true,
      showTotal: (total) => `${total} sources`,
    });

  const [recordsLoading, setRecordsLoading] = React.useState<boolean>(false);
  const [records, setRecords] = React.useState<SpmIntegrationRecord[]>([]);
  const [mediation, setMediation] = React.useState<string | undefined>(
    searchParams.get("mediation") || undefined
  );
  const [target, setTarget] = React.useState<string | undefined>(
    searchParams.get("target") || undefined
  );
  // `searchText` is what the box shows; `query` is what the table is actually filtered by, fed
  // from it on a debounce (repo convention — see TableQueryFilter). Submit-on-Enter was the
  // alternative, but it lets the visible text disagree with the rows on screen.
  const [searchText, setSearchText] = React.useState<string>("");
  const [query, setQuery] = React.useState<string>("");
  // Server-side sort state for the Materialized table — `sortField` is the column's
  // `dataIndex` (looked up in `RECORD_SORT_KEY_BY_DATA_INDEX` to get the backend `sort` key),
  // `sortOrder` is AntD's own vocabulary so it can be fed straight back into each column's
  // controlled `sortOrder` prop.
  const [sortField, setSortField] = React.useState<string | undefined>();
  const [sortOrder, setSortOrder] = React.useState<
    "ascend" | "descend" | undefined
  >();
  const [recordsPagination, setRecordsPagination] =
    React.useState<TablePaginationConfig>({
      current: 1,
      pageSize: 20,
      showSizeChanger: true,
      showTotal: (total) => `${total} materialized rows`,
    });

  const sourcesPage = sourcesPagination.current;
  const sourcesPer = sourcesPagination.pageSize;

  // Same monotonic-request-counter pattern as `fetchRecords` below — paging/resizing this table
  // fires overlapping requests with no ordering guarantee from the API, so only the newest one
  // may commit its response.
  const sourcesRequestId = React.useRef(0);

  const fetchSources = useCallback(() => {
    const requestId = ++sourcesRequestId.current;
    setSourcesLoading(true);
    WizardService.SpmInfo.all(
      new URLSearchParams({
        page: String(sourcesPage ?? 1),
        per: String(sourcesPer ?? 20),
      })
    )
      .then((response) => {
        if (requestId !== sourcesRequestId.current) return;
        setSources(response.items || []);
        setSourcesPagination((state) => ({
          ...state,
          current: response.metadata.page,
          pageSize: response.metadata.per,
          total: response.metadata.total,
        }));
      })
      .catch((error) => {
        if (requestId !== sourcesRequestId.current) return;
        message.error(error.message);
      })
      .finally(() => {
        // A superseded request must not clear the spinner the newer one is relying on.
        if (requestId === sourcesRequestId.current) setSourcesLoading(false);
      });
  }, [sourcesPage, sourcesPer]);

  const recordsPage = recordsPagination.current;
  const recordsPer = recordsPagination.pageSize;

  // Monotonic request counter. Filter changes fire overlapping requests and the API gives no
  // ordering guarantee — a slow `q=admob` resolving after a fast `q=unity` would repaint the
  // table with admob rows while the box reads "unity". Only the newest request may commit.
  const recordsRequestId = React.useRef(0);

  const fetchRecords = useCallback(() => {
    const requestId = ++recordsRequestId.current;
    setRecordsLoading(true);
    const params = new URLSearchParams({
      page: String(recordsPage ?? 1),
      per: String(recordsPer ?? 20),
    });
    if (mediation) params.set("mediation", mediation);
    if (target) params.set("target", target);
    if (query.trim()) params.set("q", query.trim());
    if (sortField && sortOrder) {
      const backendSortKey = RECORD_SORT_KEY_BY_DATA_INDEX[sortField];
      if (backendSortKey) {
        params.set("sort", backendSortKey);
        params.set("order", sortOrder === "descend" ? "desc" : "asc");
      }
    }

    WizardService.SpmIntegration.all(params)
      .then((response) => {
        if (requestId !== recordsRequestId.current) return;
        setRecords(response.items || []);
        setRecordsPagination((state) => ({
          ...state,
          current: response.metadata.page,
          pageSize: response.metadata.per,
          total: response.metadata.total,
        }));
      })
      .catch((error) => {
        if (requestId !== recordsRequestId.current) return;
        message.error(error.message);
      })
      .finally(() => {
        // A superseded request must not clear the spinner the newer one is relying on.
        if (requestId === recordsRequestId.current) setRecordsLoading(false);
      });
  }, [recordsPage, recordsPer, mediation, target, query, sortField, sortOrder]);

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  // Any filter change invalidates the current page number — page 7 of an unfiltered list is
  // usually out of range once a filter is applied, which would render an empty table and read
  // as "no results" instead of "wrong page".
  const resetToFirstPage = useCallback(
    () =>
      setRecordsPagination((state) =>
        state.current === 1 ? state : { ...state, current: 1 }
      ),
    []
  );

  const applyFilter = (apply: () => void) => {
    apply();
    resetToFirstPage();
  };

  // Mirrors `mediation`/`target` into the URL's search params so a filtered view stays a
  // shareable/deep-linkable link, the same round-trip `TableQueryFilter` does for its filters.
  const applyUrlFilter = (key: "mediation" | "target", value: string | undefined) => {
    const params = new URLSearchParams(searchParams);
    value ? params.set(key, value) : params.delete(key);
    setSearchParams(params);
  };

  // Materialized table's `onChange`: commits pagination as before, and additionally reads the
  // sorter AntD passes. `sorter` is a single `SorterResult` for one active column, but AntD's
  // type allows an array (multi-column sort) — none of these columns opt into `sorter: {
  // multiple }`, so that shape should not occur here, but we normalise defensively (take the
  // first entry) rather than crash if it ever does.
  const handleRecordsTableChange = (
    pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<RecordRow> | SorterResult<RecordRow>[]
  ) => {
    setRecordsPagination((s) => ({ ...s, ...pagination }));

    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextField = activeSorter?.order
      ? (activeSorter.field as string)
      : undefined;
    const nextOrder = activeSorter?.order ?? undefined;

    if (nextField !== sortField || nextOrder !== sortOrder) {
      setSortField(nextField);
      setSortOrder(nextOrder);
      // Same reasoning as `applyFilter`/`resetToFirstPage` above: a changed sort invalidates
      // the current page number just like a changed filter does.
      resetToFirstPage();
    }
  };

  const debouncedApplyQuery = useMemo(
    () =>
      debounce((value: string) => {
        setQuery(value);
        resetToFirstPage();
      }, 400),
    [resetToFirstPage]
  );

  useEffect(() => {
    debouncedApplyQuery(searchText);
    return () => debouncedApplyQuery.cancel();
  }, [searchText, debouncedApplyQuery]);

  const sourceColumns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 80,
      render: (id: number) => <Text type="secondary"># {id}</Text>,
    },
    {
      title: "Mapping",
      dataIndex: "mappingId",
      width: 120,
      render: (mappingId: number) => (
        <Link to={`/wizard/status/${mappingId}`}># {mappingId}</Link>
      ),
    },
    {
      title: "Engine",
      dataIndex: "engine",
      width: 130,
      render: (engine: string) => <Tag color="blue">{engine}</Tag>,
    },
    { title: "Product", dataIndex: "product" },
    {
      title: "URL",
      dataIndex: "url",
      ellipsis: true,
      render: (url: string) => {
        const href = safeHref(url);
        return href ? (
          <a href={href} target="_blank" rel="noreferrer">
            {url}
          </a>
        ) : (
          <Text>{url}</Text>
        );
      },
    },
  ];

  const recordColumns = [
    {
      title: "ID",
      dataIndex: "id",
      width: 70,
      render: (id: number) => <Text type="secondary"># {id}</Text>,
    },
    {
      // Deliberately not shrunk to the same degree as Platform/Version/Replaces Pod — this is
      // the column an operator scans first — but still an explicit width, not "auto": under
      // `scroll.x` as a fixed total (below), an "auto" column would only get a share of
      // whatever's left after the fixed columns, and Target needs more of that than an even
      // split would give it.
      title: "Target",
      dataIndex: "targetStatus",
      key: "targetStatus",
      width: 200,
      sorter: true,
      sortOrder: sortField === "targetStatus" ? sortOrder : null,
      render: (_: string, record: SpmIntegrationRecord) => (
        <Space direction="vertical" size={0}>
          <Link to={targetHref(record)}>
            {record.targetStatus || `# ${record.targetId}`}
          </Link>
          <Tag color={record.targetKind === "adapter" ? "geekblue" : "purple"}>
            {record.targetKind}
          </Tag>
        </Space>
      ),
    },
    {
      // SPM is iOS-only, so this column should read `ios` on every row. That is exactly why it
      // is here: anything else (`android`, or blank from a missed anchor join) is an anomaly the
      // operator needs to see, and this is the only screen that can show it.
      title: (
        <Tooltip title="Platform of the anchor. SPM is iOS-only — anything but `ios` is a bug.">
          Platform
        </Tooltip>
      ),
      dataIndex: "platform",
      key: "platform",
      width: 90,
      sorter: true,
      sortOrder: sortField === "platform" ? sortOrder : null,
      render: (platform: string) =>
        platform === "ios" ? (
          <Tag>{platform}</Tag>
        ) : (
          <Tag color="red">{platform || "unknown"}</Tag>
        ),
    },
    {
      // Also links to the anchor — same destination as the Target column above, via the shared
      // `targetHref` helper — so a row can be opened from either its target or its product name.
      title: "Product",
      dataIndex: "product",
      key: "product",
      width: 260,
      sorter: true,
      sortOrder: sortField === "product" ? sortOrder : null,
      ellipsis: true,
      render: (product: string, record: SpmIntegrationRecord) => (
        <Tooltip title={product}>
          <Link to={targetHref(record)}>{product}</Link>
        </Tooltip>
      ),
    },
    {
      title: "Version",
      dataIndex: "version",
      key: "version",
      width: 130,
      sorter: true,
      sortOrder: sortField === "version" ? sortOrder : null,
      render: (version: string, record: SpmIntegrationRecord) => (
        <Space direction="vertical" size={0}>
          <Text>{record.targetVersion}</Text>
          <Tooltip title="Raw git tag as published by the Swift package — intentionally not normalised to semver.">
            <Text type="secondary" code style={{ fontSize: 12 }}>
              {version}
            </Text>
          </Tooltip>
        </Space>
      ),
    },
    {
      title: "Replaces Pod",
      dataIndex: "replacesPod",
      width: 140,
      ellipsis: true,
      render: (pod?: string) =>
        pod ? (
          <Tooltip title={pod}>{pod}</Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      // Given the remaining width budget — the narrow columns above all take an explicit
      // `width` so AntD stops handing them extra space that belongs here.
      title: "URL",
      dataIndex: "url",
      ellipsis: true,
      render: (url: string) => {
        const href = safeHref(url);
        return (
          <Tooltip title={url}>
            {href ? (
              <a href={href} target="_blank" rel="noreferrer">
                {url}
              </a>
            ) : (
              <Text>{url}</Text>
            )}
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Layout>
      <Content>
        <PageHeader title="SPM" subTitle="Swift Package Manager" />

        <Alert
          type="info"
          showIcon
          style={{ marginBottom: "1rem" }}
          message="Sources are the crawler's input, materialized rows are its output"
          description={
            <>
              A <b>source</b> is hand-authored config on a Mapping. A{" "}
              <b>materialized row</b> is what the SPM crawler resolved from it and
              bound to an adapter or SDK. A source with no materialized rows means
              the crawl produced nothing bindable — run the SPM crawler from the{" "}
              <Link to="/wizard/crawler">Crawler</Link> page and check the{" "}
              <code>skipped</code> counter.
            </>
          }
        />

        <ResponsiveCard title={<Tooltip title={SOURCES_DOC}>Sources</Tooltip>}>
          <Table
            loading={sourcesLoading}
            columns={sourceColumns}
            scroll={{ x: true }}
            pagination={sourcesPagination}
            onChange={(p) => setSourcesPagination((s) => ({ ...s, ...p }))}
            dataSource={sources.map((r) => ({ ...r, key: r.id }))}
          />
        </ResponsiveCard>

        <ResponsiveCard
          style={{ marginTop: "1rem" }}
          title={<Tooltip title={MATERIALIZED_DOC}>Materialized</Tooltip>}
        >
          <Row gutter={[8, 8]} style={{ marginBottom: "1rem" }}>
            <Col>
              <Select
                allowClear
                style={{ width: 200 }}
                placeholder="Mediation"
                value={mediation}
                onChange={(value) =>
                  applyFilter(() => {
                    setMediation(value);
                    applyUrlFilter("mediation", value);
                  })
                }
                options={MEDIATION_ENGINES.map((e) => ({
                  value: e.engine,
                  label: e.label,
                }))}
              />
            </Col>
            <Col>
              <Select
                allowClear
                style={{ width: 160 }}
                placeholder="Target kind"
                value={target}
                onChange={(value) =>
                  applyFilter(() => {
                    setTarget(value);
                    applyUrlFilter("target", value);
                  })
                }
                options={[
                  { value: "adapter", label: "Adapter" },
                  { value: "sdk", label: "SDK" },
                ]}
              />
            </Col>
            <Col flex="auto">
              <Input
                allowClear
                placeholder="Search target / product / URL / version"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Col>
          </Row>
          <Table
            loading={recordsLoading}
            columns={recordColumns}
            // `x: true` does not make AntD honour each column's explicit `width` in this
            // version — measured against the live page, it rendered ID at ~46px (wrapping
            // "138" into four stacked digits) and other declared widths were ignored too.
            // A numeric total is AntD's documented fix for "some fixed columns + one column
            // that should take the rest, with real horizontal scroll for whatever doesn't
            // fit": it forces `table-layout: fixed`, so every declared `width` above is a
            // hard cap (ellipsis actually engages instead of the column growing to fit its
            // widest cell), and URL — the only column here left without a `width` — absorbs
            // whatever total width is left over. 1300 is 70+200+90+260+130+140 (ID..Replaces
            // Pod) plus ~400px of headroom for URL before scrolling kicks in.
            scroll={{ x: 1300 }}
            pagination={recordsPagination}
            onChange={handleRecordsTableChange}
            dataSource={records.map((r) => ({ ...r, key: r.id }))}
          />
        </ResponsiveCard>
      </Content>
    </Layout>
  );
};
